import React from 'react';

/**
 * Flow-owned draft / calculator state for Planting Assistant.
 * Saved-group list + inventory live in usePlantingPlansLibraryState.
 */
export function usePlantingPlanFlowState() {
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [draftManualAcres, setDraftManualAcres] = React.useState('');
  const [draftPlace, setDraftPlace] = React.useState(null);
  const [draftPlaceQuery, setDraftPlaceQuery] = React.useState('');
  const [draftWizardAnswers, setDraftWizardAnswers] = React.useState(() => ({
    goal: '',
    season: '',
    sun: '',
    equip: '',
    budget: '',
  }));
  const [draftWizardIndex, setDraftWizardIndex] = React.useState(0);
  const [draftBlendKey, setDraftBlendKey] = React.useState('');
  const [draftPlanPreview, setDraftPlanPreview] = React.useState(null);
  const [draftGroupName, setDraftGroupName] = React.useState('');
  const [draftGroupNote, setDraftGroupNote] = React.useState('');
  const [draftZoneSource, setDraftZoneSource] = React.useState('');
  const [draftZoneCode, setDraftZoneCode] = React.useState('');
  const [draftSoilType, setDraftSoilType] = React.useState('');
  const [draftSoilTypeSource, setDraftSoilTypeSource] = React.useState('');
  const [draftSoilDrainage, setDraftSoilDrainage] = React.useState('');
  const [draftSoilDrainageSource, setDraftSoilDrainageSource] = React.useState('');
  const [draftSoilPh, setDraftSoilPh] = React.useState('');
  const [draftSoilPhSource, setDraftSoilPhSource] = React.useState('');
  const [draftSoilNotes, setDraftSoilNotes] = React.useState('');
  const [draftSoilSeries, setDraftSoilSeries] = React.useState('');
  const [draftSoilDrainageRaw, setDraftSoilDrainageRaw] = React.useState('');
  const [draftSoilSurveyCovered, setDraftSoilSurveyCovered] = React.useState(false);
  const [draftFrostLastLabel, setDraftFrostLastLabel] = React.useState('');
  const [draftFrostFirstLabel, setDraftFrostFirstLabel] = React.useState('');
  const [draftFrostSource, setDraftFrostSource] = React.useState('');
  const [groundSurveyResetToken, setGroundSurveyResetToken] = React.useState(0);
  const [phHelperText, setPhHelperText] = React.useState('');
  const [calculatorGroupId, setCalculatorGroupId] = React.useState(null);
  const [calculatorMode, setCalculatorMode] = React.useState('compose');
  const [selectedSeedId, setSelectedSeedId] = React.useState('');
  const [selectedFertilizerId, setSelectedFertilizerId] = React.useState('');
  const [recommendationResult, setRecommendationResult] = React.useState(null);
  const [recommendationLoading, setRecommendationLoading] = React.useState(false);
  const [recommendationError, setRecommendationError] = React.useState(null);
  const [autoRecalcQueued, setAutoRecalcQueued] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);
  const [savingGroup, setSavingGroup] = React.useState(false);
  const [editingSaveLoading, setEditingSaveLoading] = React.useState(false);
  const [editingError, setEditingError] = React.useState(null);
  const [detectZoneLoading, setDetectZoneLoading] = React.useState(false);
  const [detectZoneError, setDetectZoneError] = React.useState(null);
  const [exportPdfLoading, setExportPdfLoading] = React.useState(false);
  const [groupOptions, setGroupOptions] = React.useState({
    zones: [],
    soilTypes: [],
    soilDrainage: [],
    soilPhGuidance: {},
    soilNotePlaceholder: '',
    seedPresets: [],
    fertilizerPresets: [],
    frostWindows: {},
  });
  const [optionsLoading, setOptionsLoading] = React.useState(true);
  const [optionsError, setOptionsError] = React.useState(null);

  return {
    selectedIds,
    setSelectedIds,
    draftManualAcres,
    setDraftManualAcres,
    draftPlace,
    setDraftPlace,
    draftPlaceQuery,
    setDraftPlaceQuery,
    draftWizardAnswers,
    setDraftWizardAnswers,
    draftWizardIndex,
    setDraftWizardIndex,
    draftBlendKey,
    setDraftBlendKey,
    draftPlanPreview,
    setDraftPlanPreview,
    draftGroupName,
    setDraftGroupName,
    draftGroupNote,
    setDraftGroupNote,
    draftZoneSource,
    setDraftZoneSource,
    draftZoneCode,
    setDraftZoneCode,
    draftSoilType,
    setDraftSoilType,
    draftSoilTypeSource,
    setDraftSoilTypeSource,
    draftSoilDrainage,
    setDraftSoilDrainage,
    draftSoilDrainageSource,
    setDraftSoilDrainageSource,
    draftSoilPh,
    setDraftSoilPh,
    draftSoilPhSource,
    setDraftSoilPhSource,
    draftSoilNotes,
    setDraftSoilNotes,
    draftSoilSeries,
    setDraftSoilSeries,
    draftSoilDrainageRaw,
    setDraftSoilDrainageRaw,
    draftSoilSurveyCovered,
    setDraftSoilSurveyCovered,
    draftFrostLastLabel,
    setDraftFrostLastLabel,
    draftFrostFirstLabel,
    setDraftFrostFirstLabel,
    draftFrostSource,
    setDraftFrostSource,
    groundSurveyResetToken,
    setGroundSurveyResetToken,
    phHelperText,
    setPhHelperText,
    calculatorGroupId,
    setCalculatorGroupId,
    calculatorMode,
    setCalculatorMode,
    selectedSeedId,
    setSelectedSeedId,
    selectedFertilizerId,
    setSelectedFertilizerId,
    recommendationResult,
    setRecommendationResult,
    recommendationLoading,
    setRecommendationLoading,
    recommendationError,
    setRecommendationError,
    autoRecalcQueued,
    setAutoRecalcQueued,
    saveError,
    setSaveError,
    savingGroup,
    setSavingGroup,
    editingSaveLoading,
    setEditingSaveLoading,
    editingError,
    setEditingError,
    detectZoneLoading,
    setDetectZoneLoading,
    detectZoneError,
    setDetectZoneError,
    exportPdfLoading,
    setExportPdfLoading,
    groupOptions,
    setGroupOptions,
    optionsLoading,
    setOptionsLoading,
    optionsError,
    setOptionsError,
  };
}
